import React from 'react'
import { Award, Download } from 'lucide-react'
import { downloadCertificatePdf } from '@lib/generateCertificatePdf'
import { CERTIFICATION_BTN_OUTLINE_DARK } from '@lib/certificationUi'

export default function CertificatePanel({
  quizTitle,
  displayName,
  scoreText,
  completedDate,
  certificateId,
  trackLabel
}) {
  const dateStr =
    completedDate ||
    new Date().toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })

  const handlePrint = () => {
    window.print()
  }

  const handlePdf = async () => {
    try {
      await downloadCertificatePdf({
        certificateId,
        recipientName: displayName,
        quizTitle,
        completedDate: dateStr,
        scoreText,
        trackLabel
      })
    } catch (err) {
      console.error('certificate pdf', err)
      window.alert('Could not generate the PDF. Please try again or use Print to save a copy.')
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 cert-print-hide">
        <h3 className="text-heading-1 text-white flex items-center gap-2">
          <Award className="w-6 h-6 text-primary-400" />
          Certificate of completion
        </h3>
        <div className="flex flex-col sm:flex-row gap-2">
          <button type="button" onClick={handlePdf} className="btn-accent inline-flex items-center justify-center gap-2 shrink-0">
            <Download className="w-4 h-4" />
            Download PDF
          </button>
          <button type="button" onClick={handlePrint} className={`${CERTIFICATION_BTN_OUTLINE_DARK} shrink-0 gap-2`}>
            Print
          </button>
        </div>
      </div>

      <div
        id="trp-certificate-print-root"
        className="rounded-2xl border-2 border-primary-500/40 bg-gradient-to-br from-navy-950 via-black to-navy-900 p-8 sm:p-12 text-center shadow-2xl"
      >
        <div className="mb-6 flex justify-center">
          <img
            src="/TRP_logo_compressed.png"
            alt="Total Rewards Pro"
            className="h-14 sm:h-16 w-auto max-w-[min(100%,280px)] object-contain opacity-95"
          />
        </div>
        <p className="text-xs uppercase tracking-[0.2em] text-primary-400 font-semibold mb-2">Total Rewards Pro</p>
        <p className="text-display-3 text-white mb-2">{quizTitle}</p>
        <p className="text-xs text-neutral-500 font-mono mb-6 break-all">ID: {certificateId}</p>
        <p className="text-body text-neutral-400 mb-2">This certifies that</p>
        <p className="text-2xl sm:text-3xl font-semibold text-white mb-6 border-b border-primary-500/30 pb-6 inline-block min-w-[200px]">
          {displayName || 'Participant'}
        </p>
        <p className="text-body text-neutral-300 max-w-lg mx-auto mb-4">
          has successfully completed the practice competency assessment ({trackLabel}) with a score of{' '}
          <span className="text-primary-400 font-semibold">{scoreText}</span>.
        </p>
        <p className="text-sm text-neutral-500">Completed on {dateStr}</p>
        <div className="mt-10 pt-8 border-t border-primary-500/25 max-w-lg mx-auto text-sm text-neutral-300 space-y-4">
          <p>
            <span className="text-neutral-500 block text-xs uppercase tracking-wider mb-1">Issuing company</span>
            <span className="text-white font-semibold">iTotal Rewards Pro LLP</span>
          </p>
          <p>
            <span className="text-neutral-500 block text-xs uppercase tracking-wider mb-1">Approved by</span>
            <span className="text-white font-semibold">Manit Mittal</span>
          </p>
        </div>
        <p className="text-xs text-neutral-600 mt-6 max-w-md mx-auto leading-relaxed">
          Practice competency assessment issued for professional development. Not an accredited third-party industry
          certification. Not legal or statutory advice.
        </p>
      </div>
    </div>
  )
}
